<script lang="ts">
    import dayjs from 'dayjs';
    import {
        VpReturnIcon,
        VpCalendarIcon,
        VpGroupIcon,
        VpMapPinIcon,
        VpRunIcon,
        VpRefreshIcon,
    } from '@decathlon-experience/icons/svelte';
    import { isDateSelectionFullMonth } from '$saga/views/Search/utils';

    import Button from '$saga/components/Button.svelte';
    import { resetLabels, resetValues } from '$saga/views/Search/utils';
    import {
        filtersContext,
        filteredToursCount,
    } from '$saga/views/Search/store';
    import { FilterType } from '../types';
    import SecondaryFiltersContent from '$saga/views/Search/Filters/SecondaryFiltersContent.svelte';

    export let handleSelectedFilter: (filterType: FilterType) => void;

    const handleResetValues = () => {
        $filtersContext.formLabels = resetLabels();
        $filtersContext.formValues = resetValues();
    };
</script>

<div class="flex h-full w-full flex-col">
    <div
        class="no-scrollbar flex h-full w-full flex-col gap-m overflow-y-scroll"
    >
        <div
            class="w-full px-m pt-m text-center text-[22px] font-semibold desktop:text-[26px]"
        >
            {#if $filteredToursCount !== null}
                <div class="italic text-content-brand">
                    {$filteredToursCount} séjour{$filteredToursCount > 1
                        ? 's'
                        : ''}
                </div>
                répond{$filteredToursCount > 1 ? 'ent' : ''} à vos envies
            {/if}
        </div>

        <button
            type="button"
            class="flex w-full items-center justify-between gap-m self-center bg-container-quiet px-xs py-l hover:bg-container-commercial-neutral"
            on:click={() => handleSelectedFilter(FilterType.SPORT)}
        >
            <span class="flex items-center gap-xs">
                <VpRunIcon class="size-l" />
                Sport
            </span>
            {#if $filtersContext.formLabels.theme.length > 0}
                {#if $filtersContext.formValues.theme.length === 1 && $filtersContext.formValues.theme[0] === 'all'}
                    <span class="truncate text-sm text-content-brand">
                        Tous les sports
                    </span>
                {:else}
                    <span class="truncate text-sm text-content-brand">
                        {$filtersContext.formLabels.theme.join(', ')}
                    </span>
                {/if}
            {:else}
                <span class="text-sm text-content-quiet"
                    >Ajoutez des sports</span
                >
            {/if}
        </button>

        <button
            type="button"
            class="flex w-full items-center justify-between gap-m self-center bg-container-quiet px-xs py-l hover:bg-container-commercial-neutral"
            on:click={() => handleSelectedFilter(FilterType.DESTINATION)}
        >
            <span class="flex items-center gap-xs">
                <VpMapPinIcon class="size-l" />
                Destination
            </span>
            {#if $filtersContext.formLabels.destination.length > 0}
                {#if $filtersContext.formValues.destination.length === 1 && $filtersContext.formValues.destination[0] === 'all'}
                    <span class="truncate text-sm text-content-brand">
                        Toutes les destinations
                    </span>
                {:else}
                    <span class="truncate text-sm text-content-brand">
                        {$filtersContext.formLabels.destination.join(', ')}
                    </span>
                {/if}
            {:else}
                <span class="text-sm text-content-quiet"
                    >Ajoutez des destinations</span
                >
            {/if}
        </button>

        <button
            type="button"
            class="flex w-full items-center justify-between gap-m self-center bg-container-quiet px-xs py-l hover:bg-container-commercial-neutral"
            on:click={() => handleSelectedFilter(FilterType.STARTDATE)}
        >
            <span class="flex items-center gap-xs">
                <VpCalendarIcon class="size-l" />
                Date de départ
            </span>
            {#if $filtersContext.formLabels.startDate != null}
                <span class="whitespace-nowrap text-sm text-content-brand">
                    {#if isDateSelectionFullMonth($filtersContext.formLabels.startDate, $filtersContext.formLabels.endDate)}
                        {dayjs(
                            $filtersContext.formLabels.startDate.toString(),
                        ).format('MMM YYYY')}
                    {:else}
                        {dayjs(
                            $filtersContext.formLabels.startDate.toString(),
                        ).format('DD MMM YYYY')} - {dayjs(
                            $filtersContext.formLabels.endDate?.toString(),
                        ).format('DD MMM YYYY')}
                    {/if}
                </span>
            {:else}
                <span class="text-sm text-content-quiet"
                    >Ajoutez un départ
                </span>
            {/if}
        </button>
        <button
            type="button"
            class="flex w-full items-center justify-between gap-m self-center bg-container-quiet px-xs py-l hover:bg-container-commercial-neutral"
            on:click={() => handleSelectedFilter(FilterType.PAX)}
        >
            <span class="flex items-center gap-xs">
                <VpGroupIcon class="size-l" />
                Voyageurs
            </span>
            {#if $filtersContext.formLabels.nbPax !== '' && $filtersContext.formLabels.nbPax !== '0'}
                <span class="truncate text-sm text-content-brand">
                    {$filtersContext.formLabels.nbPax}
                    {Number($filtersContext.formLabels.nbPax) > 1
                        ? 'voyageurs'
                        : 'voyageur'}
                </span>
            {:else}
                <span class="text-sm text-content-quiet"
                    >Ajoutez un voyageur</span
                >
            {/if}
        </button>
        {#if !$filtersContext.isHomePage}
            <SecondaryFiltersContent />
        {/if}
    </div>

    <div
        class="sticky bottom-0 left-0 flex w-full justify-between border-t border-solid border-border-quiet bg-white py-s"
    >
        <Button
            size="small"
            intent="secondary"
            shape="rounded"
            type="button"
            on:click={() => handleResetValues()}
            startIcon={VpReturnIcon}>Tout effacer</Button
        >

        <Button
            type={$filtersContext.isHomePage ? 'submit' : 'button'}
            form="travel-search-engine-form"
            size="small"
            shape="rounded"
            on:click={() => {
                if (!$filtersContext.isHomePage) {
                    const form = document.getElementById(
                        'travel-search-engine-form',
                    );
                    if (form) {
                        setTimeout(() => {
                            form.dispatchEvent(
                                new Event('submit', {
                                    bubbles: true,
                                    cancelable: true,
                                }),
                            );
                        }, 0);
                    }
                }
            }}
        >
            {#if $filtersContext.isSubmitting}
                <VpRefreshIcon class="size-m animate-spin " />
            {:else}
                Rechercher {#if $filteredToursCount !== null}
                    ({$filteredToursCount}){/if}
            {/if}
        </Button>
    </div>
</div>
